import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, LayoutDashboard, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { UserAccountMenu } from "@/components/layout/UserAccountMenu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { trackCTAClick, trackMobileMenuToggle, trackNavLinkClick } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickByLanguage } from "@/lib/language";
import qlivarLogo from "@/assets/qlivar-v2.svg";

type NavLinkItem = {
  href: string;
  label: string;
};

const MARKETING_LANDING_PATHS = new Set([
  "/",
  "/pricing",
  "/analytics",
  "/mini-store",
  "/custom-domain",
  "/for-creators",
  "/linktree-alternative",
]);

const getNavLinks = (language: "pt" | "en"): NavLinkItem[] => [
  {
    href: "#story",
    label: pickByLanguage(language, { pt: "Como funciona", en: "How it works" }),
  },
  {
    href: "#features",
    label: pickByLanguage(language, { pt: "Recursos", en: "Features" }),
  },
  {
    href: "#pricing",
    label: pickByLanguage(language, { pt: "Preços", en: "Pricing" }),
  },
  {
    href: "#affiliates",
    label: pickByLanguage(language, { pt: "Afiliados", en: "Affiliates" }),
  },
  {
    href: "#faq",
    label: "FAQ",
  },
];

export function Header() {
  const { language } = useLanguage();
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = getNavLinks(language);
  const authPath = `/auth${location.search}`;
  const signupParams = new URLSearchParams(location.search);
  signupParams.set("mode", "signup");
  const signupPath = `/auth?${signupParams.toString()}`;

  const getHashTarget = (hash: string) =>
    `${MARKETING_LANDING_PATHS.has(location.pathname) ? location.pathname : "/"}${hash}`;

  const handleHashNavigation = (label: string, hash: string, placement: "desktop_nav" | "mobile_menu") => {
    trackNavLinkClick(label, hash, placement);
    navigate(getHashTarget(hash));
    setMobileOpen(false);
  };

  const handleAuthNavigation = (label: string, path: string, placement: "header" | "mobile_menu") => {
    trackCTAClick(label, placement, path);
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <motion.header
      id="site-header"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="fixed inset-x-0 top-0 z-50 border-b border-[#eadbcc] bg-[#fff8f0]/90 backdrop-blur-xl"
    >
      <div className="container flex h-[74px] items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 text-[#2f241e] transition-opacity hover:opacity-90">
          <img src={qlivarLogo} alt="Qlivar" className="h-9 w-9" />
          <span className="font-editorial text-[1.7rem] leading-none tracking-[-0.03em]">
            Qlivar
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleHashNavigation(link.label, link.href, "desktop_nav")}
              className="text-sm font-medium text-[#5f5349] transition-colors hover:text-[#251c17]"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 sm:gap-3">
          {loading ? (
            <div className="hidden items-center gap-2 sm:flex">
              <span className="h-10 w-20 animate-pulse motion-reduce:animate-none rounded-full bg-[#efe1d4]" />
              <span className="h-10 w-28 animate-pulse motion-reduce:animate-none rounded-full bg-[#efe1d4]" />
            </div>
          ) : user ? (
            <>
              <Button
                variant="outline"
                size="sm"
                className="hidden rounded-full border-[#eadbcc] bg-[#fff8f0]/80 px-4 text-[#2f241e] transition-colors duration-150 hover:border-[#dcc5b0] hover:bg-[#fff3e9] sm:inline-flex"
                onClick={() => handleAuthNavigation("Dashboard", "/dashboard", "header")}
              >
                <LayoutDashboard className="h-4 w-4" />
                {pickByLanguage(language, { pt: "Painel", en: "Dashboard" })}
              </Button>
              <UserAccountMenu />
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="hidden rounded-full px-3 text-[#5f5349] hover:bg-[#f6ebdf] hover:text-[#2f241e] sm:inline-flex"
                onClick={() => handleAuthNavigation(pickByLanguage(language, { pt: "Entrar", en: "Log in" }), authPath, "header")}
              >
                {pickByLanguage(language, { pt: "Entrar", en: "Log in" })}
              </Button>
              <Button
                variant="hero"
                size="sm"
                className="hidden rounded-full px-4 text-sm shadow-[0_16px_35px_rgba(240,127,69,0.24)] transition-transform duration-150 ease-out hover:brightness-95 active:scale-[0.97] sm:inline-flex"
                onClick={() => handleAuthNavigation(pickByLanguage(language, { pt: "Começar grátis", en: "Start free" }), signupPath, "header")}
              >
                {pickByLanguage(language, { pt: "Começar grátis", en: "Start free" })}
              </Button>
            </>
          )}

          <Sheet
            open={mobileOpen}
            onOpenChange={(open) => {
              setMobileOpen(open);
              trackMobileMenuToggle(open);
            }}
          >
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-full border border-[#eadbcc] bg-white/80 text-[#2f241e] hover:bg-[#f8efe6] lg:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">{pickByLanguage(language, { pt: "Abrir menu", en: "Open menu" })}</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full max-w-[320px] border-l-[#eadbcc] bg-[#fffaf5] px-0">
              <div className="flex h-full flex-col">
                <div className="flex items-center justify-between border-b border-[#eadbcc] px-5 py-4">
                  <div className="flex items-center gap-3 text-[#2f241e]">
                    <img src={qlivarLogo} alt="Qlivar" className="h-9 w-9" />
                    <span className="font-editorial text-[1.6rem] leading-none">Qlivar</span>
                  </div>
                  <SheetClose asChild>
                    <button className="flex h-10 w-10 items-center justify-center rounded-full border border-[#eadbcc] text-[#5f5349] transition-colors hover:bg-[#f6ebdf] hover:text-[#2f241e]">
                      <X className="h-4 w-4" />
                      <span className="sr-only">{pickByLanguage(language, { pt: "Fechar menu", en: "Close menu" })}</span>
                    </button>
                  </SheetClose>
                </div>

                <div className="flex-1 px-5 py-6">
                  <div className="space-y-2">
                    {navLinks.map((link) => (
                      <button
                        key={link.href}
                        onClick={() => handleHashNavigation(link.label, link.href, "mobile_menu")}
                        className="flex w-full items-center justify-between rounded-[22px] border border-[#eadbcc] bg-white px-4 py-3 text-left text-sm font-medium text-[#2f241e] shadow-[0_18px_45px_rgba(111,78,54,0.06)] transition-colors duration-150 hover:bg-[#fdf5ee]"
                      >
                        <span>{link.label}</span>
                        <ArrowRight className="h-4 w-4 text-[#f07f45]" />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#eadbcc] px-5 py-5">
                  {loading ? null : user ? (
                    <Button
                      variant="outline"
                      className="w-full rounded-full border-[#dcc5b0] bg-white text-[#2f241e] hover:bg-white"
                      onClick={() => handleAuthNavigation("Dashboard", "/dashboard", "mobile_menu")}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {pickByLanguage(language, { pt: "Abrir painel", en: "Open dashboard" })}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <Button
                        variant="hero"
                        className="w-full rounded-full shadow-[0_16px_35px_rgba(240,127,69,0.24)]"
                        onClick={() => handleAuthNavigation(pickByLanguage(language, { pt: "Começar grátis", en: "Start free" }), signupPath, "mobile_menu")}
                      >
                        {pickByLanguage(language, { pt: "Começar grátis", en: "Start free" })}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full rounded-full border-[#dcc5b0] bg-white text-[#2f241e]"
                        onClick={() => handleAuthNavigation(pickByLanguage(language, { pt: "Entrar", en: "Log in" }), authPath, "mobile_menu")}
                      >
                        {pickByLanguage(language, { pt: "Entrar", en: "Log in" })}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}
